import { Price } from '../../components';
import { getMainConfig } from '../../config';
import { centsToFixed2 } from '../utils';
import { dataLayerEventAddShippingInfo, dataLayerEventAddToCart, dataLayerEventInitiateCheckout, dataLayerEventPurchase } from './dataLayerEvents';
import { gtagEventAddCustomer, gtagEventAddShippingInfo, gtagEventAddToCart, gtagEventInitiateCheckout, gtagEventPurchase } from './gtagEvents';
import { metaAddCustomer, metaEventAddToCart, metaEventPurchase, metaInitiateCheckout } from './metaEvents';

export const hookCheckoutSuccess = (bookingId: string, actionPayload: any, currency: string) => {
  if (!actionPayload) return;

  const { hooks } = getMainConfig();
  if (!hooks) return;

  const eventID = 'purchase_' + bookingId;

  if (hooks.enableMetaTracking && typeof fbq === 'function') {
    metaEventPurchase(bookingId, actionPayload, eventID);
  }

  if (hooks.enableGtagTracking && typeof gtag === 'function') {
    gtagEventPurchase(bookingId, actionPayload, currency);
  }

  if (hooks.gtagAdsSendTo && typeof gtag === 'function') {
    gtagEventPurchase(bookingId, actionPayload, currency, hooks.gtagAdsSendTo);
  }

  if (hooks.enableDataLayer) {
    dataLayerEventPurchase(bookingId, actionPayload, currency);
  }

  if (hooks.onCheckoutSuccess) {
    const ecommerceData = {
      purchase: {
        transaction_id: bookingId,
        value: centsToFixed2(actionPayload?.cart?.discountedPriceWithoutDeposit.gross), // Total transaction value (incl. tax and shipping)
        tax: centsToFixed2(actionPayload?.cart?.discountedPriceWithoutDeposit.vat),
        currency: actionPayload?.system?.unitsConfig?.currency,
        items: actionPayload?.cart?.items?.map(item => ({
          item_id: item.itemId,
          item_name: item.name,
          price: centsToFixed2(item.discountedPrice?.gross),
          quantity: item.qty,
        })),
      },
    };
    const fbqData = {
      currency: actionPayload?.system?.unitsConfig?.currency,
      value: centsToFixed2(actionPayload?.cart?.discountedPriceWithoutDeposit.gross),
      content_ids: actionPayload?.cart?.items?.map(item => item.itemId),
      content_type: 'product',
      contents: actionPayload?.cart?.items.map((item: { itemId: string; name: string; qty: number; individualDiscountedPrice: Price; individualPrice: Price }) => ({
        id: item.itemId,
        quantity: item.qty,
        price: centsToFixed2(item.individualDiscountedPrice?.gross || item.individualPrice?.gross),
        item_name: item.name,
      })),
      num_items: actionPayload?.cart?.items?.length,
    };
    try {
      hooks.onCheckoutSuccess({
        price: actionPayload?.cart?.discountedPrice, //legacy to support old tracking setup
        ecommerceData,
        fbqData,
        eventID: eventID,
      });
    } catch (e) {
      console.error('[rentware] Caught exception in hook!', e);
    }
  }
};

export const hookAddToCart = (params: any, currency: string = 'EUR') => {
  if (!params) return;

  const { hooks } = getMainConfig();
  if (!hooks) return;

  if (hooks.enableGtagTracking && typeof gtag === 'function') {
    gtagEventAddToCart(params, currency);
  }

  if (hooks.enableMetaTracking && typeof fbq === 'function') {
    metaEventAddToCart(params, currency);
  }

  if (hooks.enableDataLayer) {
    dataLayerEventAddToCart(params, currency);
  }

  if (hooks.onAddToCart) {
    try {
      hooks.onAddToCart({
        productId: params.itemId,
        quantity: params.amountToAdd,
        currency: currency,
        name: params.name,
      });
    } catch (e) {
      console.error('[rentware] Caught exception in onAddToCart hook!', e);
    }
  }
};

export const hookInitiateCheckout = (bookingId: string, state: any) => {
  const { hooks } = getMainConfig();
  if (!hooks) return;

  if (hooks.enableMetaTracking && typeof fbq === 'function') {
    metaInitiateCheckout(state);
  }

  if (hooks.enableGtagTracking && typeof gtag === 'function') {
    gtagEventInitiateCheckout(state);
  }

  if (hooks.enableDataLayer) {
    dataLayerEventInitiateCheckout(state);
  }

  if (hooks.onInitiateCheckout) {
    try {
      hooks.onInitiateCheckout({ transactionTotal: state.amountToPay.total, transactionId: bookingId });
    } catch (e) {
      console.error('[rentware] Caught exception in hook!', e);
    }
  }
};

export const hookStoreBillingData = (actionPayload: any, state: any) => {
  if (!actionPayload) return;

  const { hooks } = getMainConfig();
  if (!hooks) return;

  if (hooks.metaPixelId && hooks.enableMetaTracking && typeof fbq === 'function') {
    metaAddCustomer(actionPayload, hooks.metaPixelId);
  }

  if (hooks.enableGtagUserDataTracking && typeof gtag === 'function') {
    gtagEventAddCustomer(actionPayload);
  }

  if (hooks.enableGtagTracking && typeof gtag === 'function') {
    gtagEventAddShippingInfo(state);
  }

  if (hooks.enableDataLayer) {
    dataLayerEventAddShippingInfo(state);
  }
};
